import { useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import axios from 'axios';
import * as logger from 'loglevel';
import { isSSR } from '@front10/utils';
import { annotateAndLogError } from '../../../helpers/error';
import { NOT_AVAILABLE, EMPTY_OBJECT, formatValueStringEmpty, formatDate } from './helpers';
import { useTracking } from './TrackingContext';
import { publish } from './pubsub';
import { formatter } from '@everymundo/airmodules-event-datalayer';
import { doRedirect } from './redirects';
import { mergeDeep } from '../../../helpers/utils';

export { TrackingProvider } from './TrackingContext';

interface QueryType {
  location?: string;
  siteEdition?: string;
  totalRooms?: number;
  checkinDate?: string;
  checkoutDate?: string;
  rooms?: Record<string, any>;
  currencyCode?: string;
  language?: string;
  device?: string;
  extraInfo?: Record<string, any>;
  trackingParameters?: Record<string, any>;
}

export function useContainerProps({
  fareItemInteractionType = 'popup',
  integrationSettings = EMPTY_OBJECT,
  meta = EMPTY_OBJECT,
  pageContext = EMPTY_OBJECT,
  prePopulation = EMPTY_OBJECT,
  tracking = EMPTY_OBJECT,
  filters = EMPTY_OBJECT,
  secondaryFilters = EMPTY_OBJECT,
  ratesCalendar = EMPTY_OBJECT //this must be temporary
}: $TSFixMe) {
  // const newFilters = { ...filters, ...secondaryFilters };
  const { navInfoSettings = '' } = ratesCalendar; //this must be temporary
  return useMemo(() => {
    return {
      fareItemInteractionType,

      // Tracking props
      eventType: tracking.eventType,
      eventCategory: tracking.eventCategory,
      trackingConfig: tracking.trackingConfig,
      passengerCount: tracking.passengerCount,

      // Prepopulation props
      tenantCode: prePopulation.tenantCode,
      market: prePopulation.autoSettings.market,

      // Page context
      deviceCategory: pageContext.deviceCategory,
      language: pageContext.language,
      siteEdition: pageContext.siteEdition,
      url: pageContext.url,
      currencyCode: formatValueStringEmpty(
        navInfoSettings.currencyCode ? navInfoSettings.currencyCode : '' //this must be temporary
      ),
      provider: pageContext.provider ? pageContext.provider : '',
      propertyName: pageContext.userInputPropertyName ? pageContext.userInputPropertyName : '',
      countryCode: pageContext.userInputPropertyCountryCode
        ? pageContext.userInputPropertyCountryCode
        : '',
      regionName: pageContext.userInputPropertyRegionName
        ? pageContext.userInputPropertyRegionName
        : '',
      cityName: pageContext.userInputPropertyCityName ? pageContext.userInputPropertyCityName : '',

      // Integration settings
      v2DeepLinkApiSettings: integrationSettings.v2DeepLinkApiSettings
        ? integrationSettings.v2DeepLinkApiSettings
        : null,
      integrationSettings,

      // Component metadata
      tagName: meta.tag,
      meta,
      pageContext,
      prePopulation
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fareItemInteractionType, integrationSettings, meta, pageContext, prePopulation, tracking]);
}

/**
 * This function get params in url
 * @param {*} query
 */

const getQueryParams = (query: $TSFixMe) => {
  const vars = query.split('&');
  const queryString: $TSFixMe = {};
  for (let i = 0; i < vars.length; i += 1) {
    const pair = vars[i].split('=');
    const key = decodeURIComponent(pair[0]);
    const value = decodeURIComponent(pair[1]);
    // If first entry with this name
    if (typeof queryString[key] === 'undefined') {
      queryString[key] = decodeURIComponent(value);
      // If second entry with this name
    } else if (typeof queryString[key] === 'string') {
      const arr = [queryString[key], decodeURIComponent(value)];
      queryString[key] = arr;
      // If third or later entry with this name
    } else {
      queryString[key].push(decodeURIComponent(value));
    }
  }
  return queryString;
};

/**
 * This function get params in url that contains in white list
 * @param {*} trackingParametersWhitelist
 */
function getUrlParameters(trackingParametersWhitelist: $TSFixMe) {
  if (isSSR()) {
    return {};
  }
  const trackingParameters: $TSFixMe = {};
  if (Array.isArray(trackingParametersWhitelist)) {
    const query =
      window.location.search && window.location.search.startsWith('?')
        ? window.location.search.substring(1)
        : window.location.search;
    const params = getQueryParams(query);
    Object.keys(params).map((key) => {
      if (trackingParametersWhitelist.includes(key) && params[key] !== '') {
        trackingParameters[key] = params[key];
      }
      return key;
    });
  }
  return trackingParameters;
}

function getStaticParameters(v2DeepLinkApiSettings: $TSFixMe) {
  let params: $TSFixMe = {};
  if (
    v2DeepLinkApiSettings &&
    typeof v2DeepLinkApiSettings === 'object' &&
    v2DeepLinkApiSettings.constructor === Object
  ) {
    params = getUrlParameters(v2DeepLinkApiSettings.trackingParametersWhitelist);
    if (
      v2DeepLinkApiSettings.params &&
      v2DeepLinkApiSettings.params.trackingParameters &&
      typeof v2DeepLinkApiSettings.params.trackingParameters === 'object' &&
      v2DeepLinkApiSettings.params.trackingParameters.constructor === Object
    ) {
      params = Object.assign({}, params, v2DeepLinkApiSettings.params.trackingParameters);
      Object.keys(params).map((tp) => {
        if (params[tp] === '') {
          delete params[tp];
        }
        return tp;
      });
    }
  }
  return params;
}

export function getPopupData({ container, fare, state }: $TSFixMe) {
  const { property } = state;
  const { passengerCount } = container;

  const data: $TSFixMe = {
    rooms: [
      {
        guests: {
          ADULT: {
            total: passengerCount
          }
        }
      }
    ]
    // blackoutDates: [
    //   {
    //     start: '2022-05-01',
    //     end: '2022-05-10'
    //   }
    // ],
    // promotions: [
    //   {
    //     id: 'PROMO',
    //     value: '10OFF'
    //   },
    //   {
    //     id: 'VETERAN',
    //     value: true
    //   }
    // ],
    // accessibilityNeeds: false,
    // redemptionUnit: 'POINTS'
  };

  if (state.property) {
    data.property = typeof property === 'string' ? property : property.value;
  }

  // Populate origin and destination from filters when component does not has data
  if (fare.dataType === 'EMPTY_FROM_API') {
    let departureDate = fare.departureDate ? dayjs(fare.departureDate) : null;
    const today = dayjs();
    if (!departureDate || departureDate.isBefore(today)) {
      departureDate = today;
    }
    let returnDate = departureDate.add(7, 'day');
    if (state.stayLength && state.stayLength.maximum) {
      returnDate = departureDate.add(state.stayLength.maximum, 'day');
    }

    data.checkIn = formatDate(departureDate);
    data.checkOut = formatDate(returnDate);
    return data;
  }
  // Populate data when fare is not empty
  return {
    ...data,
    checkIn: formatDate(fare.checkinDate.substring(0, 10)),
    checkOut: formatDate(fare.checkoutDate.substring(0, 10))
  };
}

export function openPopup(fare: $TSFixMe) {
  // let momentFormattedDeparture = null;
  // let momentFormattedReturn = null;
  // if (window && window.EM && window.EM.utils && window.EM.utils.PubSub) {
  //   // eslint-disable-next-line
  //   const fcRuntime = window.EM.___fcRuntime;
  //   if (fcRuntime && fcRuntime.vendors && fcRuntime.vendors.moment) {
  //     const infameMomentPleaseDontImportMeNever = fcRuntime.vendors.moment;
  //     momentFormattedDeparture =
  //       fare && fare.departureDate && infameMomentPleaseDontImportMeNever(fare.departureDate);
  //     momentFormattedReturn =
  //       fare && fare.returnDate && infameMomentPleaseDontImportMeNever(fare.returnDate);
  //   } else {
  //     logger.warn(
  //       `INTEGRATION_ACTIONS: **OpenPopupAction** window.EM.___fcRuntime.vendors.moment not
  //           available as global variable and the **Booking Popup** needed it`
  //     );
  //   }
  // }
  const data = { ...fare };
  // if (momentFormattedDeparture && momentFormattedDeparture.isValid()) {
  //   data.departureDate = momentFormattedDeparture;
  // }
  // if (momentFormattedReturn && momentFormattedReturn.isValid()) {
  //   data.returnDate = momentFormattedReturn;
  // }

  publish('em.hospitality.booking.open', data);
}

// function evaluateParameter(fare: $TSFixMe, param: $TSFixMe) {
//   if (
//     param &&
//     (typeof param === 'string' || param instanceof String) &&
//     param.indexOf('@@') !== -1
//   ) {
//     const paramEvaluation = param.split('.');

//     if (paramEvaluation && paramEvaluation.length) {
//       const paramsEvalLength = paramEvaluation[1].length;
//       // fieldFare contain the field from api
//       const fieldFare = paramEvaluation[1].substr(0, paramsEvalLength - 2);

//       if (fare) {
//         return fare[fieldFare];
//       }
//     }
//   }

//   return param;
// }

function buildQuery(container: $TSFixMe, property: $TSFixMe, fare: $TSFixMe, state: $TSFixMe) {
  const {
    deviceCategory,
    siteEdition,
    language,
    v2DeepLinkApiSettings: { params }
  } = container;

  // Get external platfom parameters
  const { extraInfo } = params;
  const { v2DeepLinkApiSettings } = container;

  if (!deviceCategory) {
    logger.warn('INTEGRATION__DEEPLINK: **device** is not defined.');
  }
  if (!siteEdition) {
    logger.warn('INTEGRATION__DEEPLINK: **siteEdition** is not defined.');
  }

  if (!extraInfo) {
    logger.warn('INTEGRATION__DEEPLINK: **extraInfo** is not defined.');
  }

  if (!fare) {
    logger.warn('INTEGRATION__DEEPLINK: **fare** is empty.');
  }

  let query: QueryType = {
    siteEdition,
    language,
    totalRooms: fare && fare.roomCount,
    rooms: [
      {
        adults: 1
      }
    ],

    checkinDate:
      fare && fare.checkinDate && dayjs(fare.checkinDate.substring(0, 10)).format('YYYY-MM-DD'),
    checkoutDate:
      fare && fare.checkoutDate && dayjs(fare.checkoutDate.substring(0, 10)).format('YYYY-MM-DD'),
    // currencyCode: (fare && fare.priceSpecification.currencyCode) || currencyCode || '',
    location: (fare && fare.propertyCode) || property || '',
    device: (deviceCategory && deviceCategory.toUpperCase()) || 'DESKTOP',
    extraInfo,
    trackingParameters: getStaticParameters(v2DeepLinkApiSettings)
  };

  if (fare.dataType === 'EMPTY_FROM_API') {
    let departureDate = fare.departureDate ? dayjs(fare.departureDate) : null;
    const today = dayjs();
    if (!departureDate || departureDate.isBefore(today)) {
      departureDate = today;
    }
    let returnDate = departureDate.add(7, 'day');
    if (state.stayLength && state.stayLength.maximum) {
      returnDate = departureDate.add(state.stayLength.maximum, 'day');
    }

    query = {
      ...query,
      checkinDate: formatDate(departureDate),
      checkoutDate: formatDate(returnDate)
    };
  }
  return query;
}

export function redirect({ container, fare, state }: $TSFixMe) {
  const { property } = state;

  const query = buildQuery(container, property.value, fare, state);
  const { v2DeepLinkApiSettings } = container;
  const newV2DeepLinkApiSettings = { ...v2DeepLinkApiSettings };
  let error = '';
  if (!newV2DeepLinkApiSettings.method) {
    error += 'INTEGRATION__DEEPLINK: **method** is not defined. ';
  }
  if (!newV2DeepLinkApiSettings.endPoint) {
    error += 'INTEGRATION__DEEPLINK: **endPoint** is not defined.';
  }
  if (
    newV2DeepLinkApiSettings.endPoint.search('%%airlineCode%%') !== -1 ||
    newV2DeepLinkApiSettings.endPoint.search('@@airlineIataCode@@') !== -1
  ) {
    newV2DeepLinkApiSettings.endPoint = newV2DeepLinkApiSettings.endPoint.replace(
      '%%airlineCode%%',
      fare.airlineCode
    );
    newV2DeepLinkApiSettings.endPoint = newV2DeepLinkApiSettings.endPoint.replace(
      '@@airlineIataCode@@',
      container.tenantCode
    );
  }
  if (!newV2DeepLinkApiSettings.headers) {
    newV2DeepLinkApiSettings.headers = {};
  }
  if (error) {
    logger.error(error);
  } else {
    axios({
      method: newV2DeepLinkApiSettings.method,
      url: `${newV2DeepLinkApiSettings.endPoint}`,
      headers: { ...newV2DeepLinkApiSettings.headers },
      data: query
    })
      .then((response) => doRedirect(response.data))
      .catch((err) => {
        logger.error(
          annotateAndLogError(
            err,
            'INTEGRATION__DEEPLINK',
            false,
            false,
            container.integrationSettings,
            container.meta
          )
        );
      });
  }
}

function isEnableTrackingEvent(nameEvent: string, enableEventsName: Array<string>) {
  for (let index = 0; index < enableEventsName.length; index++) {
    const element = enableEventsName[index];
    if (element.toUpperCase() === nameEvent) {
      return true;
    }
  }
  return false;
}

function sendTrackingEvent(
  nameEvent: string,
  actionType: string,
  payload: any,
  enableEventsName: Array<string>,
  send: any
) {
  if (enableEventsName && enableEventsName.length !== 0) {
    if (isEnableTrackingEvent(actionType, enableEventsName)) {
      return send(nameEvent, payload);
    }
  } else {
    return send(nameEvent, payload);
  }
}

export function useTrackingChange() {
  const container: $TSFixMe = useTracking();
  const { eventType, fareItemInteractionType } = container;

  const {
    v2DeepLinkApiSettings: { params = {} }
  } = container;

  // Get external platfom parameters
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { extraInfo } = params;

  const track = useCallback(
    (state: $TSFixMe, action: $TSFixMe, actualState: $TSFixMe) => {
      const baseline: $TSFixMe = {
        actionLabel: '',
        tenantCode: container.tenantCode,
        provider: container.provider,
        regionName: container.regionName,
        countryCode: container.countryCode,
        cityName: container.cityName,
        propertyCode: formatValueStringEmpty(state.property ? state.property.value : null),
        propertyName: formatValueStringEmpty(
          state.property.name ? state.property.name : container.propertyName
        ),
        currencyCode: container.currencyCode,
        totalPrice: '',
        totalPriceUSD: '',
        startDate: state.dateRange.start
          ? formatDate(state.dateRange.start)
          : formatDate(container.departureDateStart),
        endDate: formatDate(state.dateRange.end),
        daysUntilBooking: '',
        tripLength: '',
        roomAccesibility: '',
        timestamp: new Date(Date.now()).toISOString(),
        url: container.url ? container.url : '',
        guest: [
          {
            count: container.passengerCount,
            adultCount: container.passengerCount,
            youngAdultCount: '',
            childCount: '',
            infantInLapCount: '',
            infantInSeatCount: ''
          }
        ],
        room: [
          {
            count: 1,
            type: ''
          }
        ],
        page: [
          {
            siteEdition: container.siteEdition,
            countryIsoCode: container.market,
            languageIsoCode: container.language
          }
        ]
      };

      // Tag name is optional
      if (container.tagName) {
        baseline.tagName = container.tagName;
      }

      let defaultTracking;
      let fareState;

      if (action.payload && action.payload.fare) {
        const { fare } = action.payload;
        fareState = {
          ...state,
          currencyCode: fare.currencyCode ? fare.currencyCode : container.currencyCode,
          totalPrice: fare.totalPrice ? fare.totalPrice.toFixed(2) : '',
          totalPriceUSD: fare.usdTotalPrice ? fare.usdTotalPrice.toFixed(2) : '',
          tripLength: fare.nightCount ? fare.nightCount : '',
          cityName: fare.locationRequest
            ? fare.locationRequest.hierarchy.city.name
            : container.cityName,
          daysUntilBooking: fare.checkoutDate
            ? dayjs(fare.checkoutDate.substring(0, 10)).diff(dayjs(), 'day')
            : ''
        };

        defaultTracking = {
          ...baseline,
          currencyCode: formatValueStringEmpty(fareState.currencyCode),
          totalPrice: formatValueStringEmpty(fareState.totalPrice),
          totalPriceUSD: formatValueStringEmpty(fareState.totalPriceUSD),
          tripLength: formatValueStringEmpty(fareState.tripLength),
          cityName: formatValueStringEmpty(fareState.cityName),
          startDate: dayjs(fare.checkinDate ? fare.checkinDate.substring(0, 10) : undefined).format(
            'YYYY-MM-DD'
          ),
          endDate: dayjs(fare.checkoutDate ? fare.checkoutDate.substring(0, 10) : undefined).format(
            'YYYY-MM-DD'
          ),
          daysUntilBooking: formatValueStringEmpty(fareState.daysUntilBooking)
        };

        if (fare.dataType === 'EMPTY_FROM_API') {
          let departureDate = fare.departureDate ? dayjs(fare.departureDate) : null;
          const today = dayjs();
          if (!departureDate || departureDate.isBefore(today)) {
            departureDate = today;
          }
          let returnDate = departureDate.add(7, 'day');
          if (state.stayLength && state.stayLength.maximum) {
            returnDate = departureDate.add(state.stayLength.maximum, 'day');
          }

          defaultTracking = {
            ...defaultTracking,
            startDate: formatDate(departureDate),
            endDate: formatDate(returnDate)
          };
        }
      }

      const formatHotels = (object: object) => {
        try {
          formatter.formatHotels(object);
        } catch (error) {
          const MESSAGE = `TRACKING: **@everymundo/airmodules-event-datalayer** ${error}`;
          logger.error(MESSAGE);
        }
      };

      const send = (name: $TSFixMe, changes: $TSFixMe) => {
        // eslint-disable-next-line no-console
        console.log('OUTPUT FOR HOTEL OBJECT', {
          module: eventType,
          event: name,
          eventAction: name,
          ...baseline,
          ...changes
        });

        // Copy of changes object for avoid overwrite from EM
        const changesCpy = mergeDeep({}, changes);

        formatHotels({
          module: eventType,
          event: name,
          eventAction: name,
          ...baseline,
          ...changesCpy
        });
      };

      const enableEventsName =
        container.trackingConfig !== undefined ? container.trackingConfig.enable : [];
      switch (action.type) {
        case 'NO_FARES_AVAILABLE': {
          sendTrackingEvent('no-fares-available', action.type, {}, enableEventsName, send);
          break;
        }
        case 'MONTH_CHANGE': {
          sendTrackingEvent(
            'select-month',
            action.type,
            {
              month: dayjs(action.payload.start).month(),
              actionLabel: dayjs(action.payload.start).format('MMMM')
            },
            enableEventsName,
            send
          );
          break;
        }
        case 'PROPERTY_CHANGE': {
          sendTrackingEvent(
            'select-property',
            action.type,
            {
              propertyCode: action.payload.value,
              propertyName: action.payload.name,
              actionLabel: action.payload.name
            },
            enableEventsName,
            send
          );
          break;
        }

        case 'STAY_LENGTH_CHANGE': {
          sendTrackingEvent(
            'select-stay-length',
            action.type,
            { stayLength: action.payload.value, actionLabel: action.payload.valueTracking },
            enableEventsName,
            send
          );
          break;
        }
        case 'RATES_TAGS_CHANGE': {
          let ratesFeatures = action.payload.map((item: any) => item.value);
          ratesFeatures = ratesFeatures.filter((item: any) => item !== 'any');
          sendTrackingEvent(
            'select-offer',
            action.type,
            {
              ratesFeatures: ratesFeatures,
              actionLabel: ratesFeatures.toString()
            },
            enableEventsName,
            send
          );
          break;
        }

        case 'DATE_RANGE_CHANGE': {
          const departureDate = formatDate(action.payload.start);
          const returnDate = formatDate(action.payload.end);

          const noChange = departureDate === returnDate;

          const type = action.payload.type ?? 'all';
          const isDeparture = type === 'all' || type === 'departure';
          const isReturn = type === 'all' || type === 'return';
          if (
            (isDeparture && departureDate !== baseline.departureDate) ||
            state.dateRange.checkedFlexibleDates !== baseline.flexibleDates
          ) {
            sendTrackingEvent(
              'departure',
              action.type,
              {
                departureDate,
                returnDate: noChange ? NOT_AVAILABLE : returnDate,
                flexibleDates: baseline.flexibleDates
              },
              enableEventsName,
              send
            );
          }
          if (
            (isReturn && returnDate !== baseline.returnDate) ||
            (isReturn &&
              returnDate !== 'n/a' &&
              state.dateRange.checkedFlexibleDates !== baseline.flexibleDates)
          ) {
            sendTrackingEvent(
              'return',
              action.type,
              {
                departureDate,
                returnDate,
                flexibleDates: baseline.flexibleDates
              },
              enableEventsName,
              send
            );
          }
          return undefined;
        }
        case 'LOAD_MORE': {
          sendTrackingEvent('more-deals', action.type, {}, enableEventsName, send);
          break;
        }
        case 'CLEAR_ALL':
        case 'CLEAR_PARTIAL': {
          sendTrackingEvent(
            'clear-all',
            action.type,
            {
              filters: 'cleared'
            },
            enableEventsName,
            send
          );
          break;
        }

        case 'FARE_CLICK': {
          const { fare } = action.payload;
          // const { fare, callToActionLabel } = action.payload;
          if (!fare) {
            return undefined;
          }
          if (typeof fare === 'object' && fare.constructor === Object) {
            if (fareItemInteractionType === 'deeplink') {
              if (enableEventsName && enableEventsName.length !== 0) {
                if (isEnableTrackingEvent(action.type, enableEventsName)) {
                  //eslint-disable-next-line no-console
                  console.log('OUTPUT FOR HOTEL OBJECT', {
                    event: 'search-initiation',
                    module: eventType,
                    eventAction: 'search-initiation',
                    ...defaultTracking,
                    actionLabel: 'calendar'
                  });

                  formatHotels({
                    event: 'search-initiation',
                    module: eventType,
                    eventAction: 'search-initiation',
                    ...defaultTracking,
                    actionLabel: 'calendar'
                  });
                }
              } else {
                // eslint-disable-next-line no-console
                console.log('OUTPUT FOR HOTEL OBJECT', {
                  event: 'open-booking-popup',
                  module: eventType,
                  eventAction: 'open-booking-popup',
                  ...defaultTracking,
                  actionLabel: 'calendar'
                });
                formatHotels({
                  event: 'open-booking-popup',
                  module: eventType,
                  eventAction: 'open-booking-popup',
                  ...defaultTracking,
                  actionLabel: 'calendar'
                });
              }

              redirect({ container, state: fareState, fare });
            }
            if (fareItemInteractionType === 'popup') {
              openPopup(getPopupData({ container, state: fareState, fare }));
              if (enableEventsName && enableEventsName.length !== 0) {
                if (isEnableTrackingEvent(action.type, enableEventsName)) {
                  // eslint-disable-next-line no-console
                  console.log('OUTPUT FOR HOTEL OBJECT', {
                    module: eventType,
                    event: 'open-booking-popup',
                    eventAction: 'open-booking-popup',
                    ...defaultTracking,
                    actionLabel: 'calendar'
                  });

                  formatHotels({
                    module: eventType,
                    event: 'open-booking-popup',
                    eventAction: 'open-booking-popup',
                    ...defaultTracking,
                    actionLabel: 'calendar'
                  });
                }
              } else {
                // eslint-disable-next-line no-console
                console.log('OUTPUT FOR HOTEL OBJECT', {
                  module: eventType,
                  event: 'open-booking-popup',
                  eventAction: 'open-booking-popup',
                  ...defaultTracking,
                  actionLabel: 'calendar'
                });

                formatHotels({
                  module: eventType,
                  event: 'open-booking-popup',
                  eventAction: 'open-booking-popup',
                  ...defaultTracking,
                  actionLabel: 'calendar'
                });
              }
            }
          }
          return undefined;
        }

        default: {
          return undefined;
        }
      }
    },
    [container, eventType, fareItemInteractionType]
  );

  return track;
}
